<template>
  <v-container
    id="components-view"
    fluid
    tag="section"
  >
    <material-card
      icon="$components"
      icon-small
      color="accent"
      :title="page_title"
    >
      <template
        v-if="!plane_filter"
        v-slot:title2
      >
        <div class="text-h5">
          <tooltip-btn
            icon="$back"
            tooltip="planes"
            @click="back"
          />
        </div>
      </template>

      <v-card-title>
        <v-btn
          v-show="editor"
          v-shortkey.once="['f2']"
          color="primary"
          class="mb-2"
          @shortkey="edit(-1)"
          @click="edit(-1)"
        >
          {{ $t('new_item',{ item: $t('maintenance_operation') }) }}
        </v-btn>
        <v-row class="ma-0">
          <v-spacer v-if="!$vuetify.breakpoint.mdAndDown" />
          <data-selector
            v-model="component_type_id"
            field-name="component_type"
            source-type="component-types"
            translate
            rules=""
            hide-details
            class="mx-2"
          />
          <data-selector
            v-if="plane_filter"
            v-model="plane_id"
            field-name="plane"
            source-type="planes"
            rules=""
            hide-details
            class="mx-2"
          />
          <v-text-field
            v-model="search"
            append-icon="$search"
            class="ml-auto"
            hide-details
            :label="$t('search_records')"
            single-line
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-data-table
          show-expand
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
        >
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="ma-3 pa-6">
              <component-informations
                :component="item"
              />
            </td>
          </template>
          <template v-slot:item.avatar="{item}">
            <v-img
              v-if="item.avatar"
              max-height="50px"
              :src="'data:image/jpeg;base64,' + item.avatar.thumbnail"
            />
          </template>
          <template #item.component_type="{item}">
            {{ $t( 'types.'+item.component_type.name) }}
          </template>
          <template #item.limitations="{item}">
            <v-row v-if="item.date_limiter" dense>
              {{ $t('next_maintenance') }}: {{ item.limit_date }}
            </v-row>
            <v-row v-if="item.flight_limiter" dense>
              {{ $t('remaining_flight_time') }}: {{ item.limit_counter_flight*60 - item.current_counter_flight | minToTimeFormat }}
            </v-row>
            <v-row v-if="item.engine_limiter" dense>
              {{ $t('remaining_engine_time') }}: {{ item.limit_counter_engine - item.current_counter_engine | numberFormat }}
            </v-row>
            <v-row v-if="item.takeoff_limiter" dense>
              {{ $t('remaining_takeoff') }}: {{ item.limit_counter_takeoff - item.current_counter_takeoff | numberFormat }}
            </v-row>
          </template>
          <template #item.actions="{item}">
            <component-operations
              :component-id="item.id"
            />
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="!action.disable && (item.organization_id === profile.organization.id || action.icon !== '$edit' )"
              :color="action.color"
              :icon="action.icon"
              :tooltip="action.tooltip"
              :to="action.to ? action.to+item.id : ''"
              @click="action.func(item.id)"
            >
              <v-icon v-text="action.icon" />
            </tooltip-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'
  import ComponentInformations from '@/layouts/default/infos/ComponentInfo'
  import ComponentOperations from '@/layouts/default/infos/ComponentOperations'

  export default {
    name: 'MaintenanceOperations',
    components: {
      ComponentOperations,
      ComponentInformations,
    },
    data () {
      return {
        expanded: [],
        singleExpand: true,
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        plane_filter: true,
        operation_type_id: null,
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      /* ...sync('maintenance/operations', {
        options: 'options',
        search: 'search',
        plane_id: 'plane_id',
      }), */
      search: sync('maintenance/operations@search'),
      options: sync('maintenance/operations@options'),
      plane_id: sync('maintenance/operations@plane_id'),
      // plane_id: sync('maintenance/operations.plane_id'),
      table_headers () {
        return [
          {
            text: this.$t('fields.maintenance_operation_name'),
            sortable: false,
            value: 'operation_type.name',
          },
          {
            text: this.$t('limitations'),
            sortable: false,
            value: 'limitations',
          },
          {
            text: this.$t('fields.plane'),
            sortable: false,
            value: 'plane.reg_num',
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          // { color: 'info', icon: '$info' },
          { color: 'action', icon: '$refresh', tooltip: 'tooltip_recalc', func: this.recalc, disable: false },
          { color: 'primary', icon: '$events', tooltip: 'tooltip_events', func: this.showEvents, disable: !this.editor },
          { color: 'blue', icon: '$documents', tooltip: 'tooltip_documents', to: '/documents/component/' },
          { color: 'edit', icon: '$edit', tooltip: 'tooltip_edit', func: this.edit, disable: !this.editor },
        ]
      },
      page_title () { return this.$t('maintenance_operations') },
      editor () { return this.profile.can['component-edit'] },
      computed_headers () { return this.table_headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.options.page = 1
            this.load()
          }, 800)
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
      plane_id: {
        handler () {
          this.load()
        },
        deep: true,
      },
      operation_type_id: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      if (this.$route.params.plane_id) {
        this.plane_filter = false
        this.plane_id = parseInt(this.$route.params.plane_id)
      }
    },
    methods: {
      getList: call('maintenance/operations'),
      _recalc: call('component/recalc'),
      getReport: call('action/getReport'),
      async load () {
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            plane_id: this.plane_id,
            operation_type_id: this.operation_type_id,
            search: this.search,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (id) {
        this.$router.push(`/maintenance/operation/${id}`).catch(() => {})
      },
      async recalc (id) {
        await this._recalc({ id: id }).then(response => {
          this.load()
        })
      },
      showEvents (id) {
        this.$router.push(`/component/${id}/events`).catch(() => {})
        // this.$router.push(`/profile/pilot-info-edit`).catch(() => {})
      },
      back () {
        this.$router.push('/maintenance/planes/').catch(() => {})
      },
    },
  }
</script>
